import classNames from "classnames";
import _ from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Mention, MentionsInput } from "react-mentions";
import { useHistory, useParams } from "react-router-dom";
import Popup from "reactjs-popup";

import { userColors } from "@/components/constants";
import { MiniTag } from "@/components/Elements";
import {
  formatTimeAfterDate,
  stringToDateAndTime,
} from "@/components/helpers/DateFormatters";
import { iconForThemisModuleIdentifier } from "@/components/helpers/iconForThemisModuleIdentifier";
import { nameFromModuleWorkspace } from "@/components/helpers/nameForThemisModuleIdentifier";
import { renderWorkspaces } from "@/components/helpers/RenderWorkspaces";
import ImmutableTaskComment from "@/components/settings/notificationsCenter/tasks/ImmutableTaskComment";
import SlideMenu from "@/components/slideMenu/SlideMenu";
import { useMainStore } from "@/contexts/Store";
import arrowDown from "@/images/table-image/icon/arrow-down-black.svg";
import arrowRight from "@/images/table-image/icon/arrow-right.svg";
import eyeIcon from "@/images/table-image/icon/eye-white.svg";
import replyIcon from "@/images/table-image/icon/reply-icon-black.svg";
import { UserComment } from "@/stores/types/comment-types";

type Props = {
  task: UserComment;
  opened?: boolean;
  selectedTaskID: number | null;
  setSelectedTaskID(taskId: number | null): void;
  workspaceID: number;
};

function SingleTask({
  task,
  workspaceID,
  selectedTaskID,
  setSelectedTaskID,
  opened,
}: Props) {
  // Import MobX stores
  const mainStore = useMainStore();

  // Hooks
  const params = useParams<{ task_id: string }>();

  const task_id = Number(params.task_id);

  // State
  const [showTask, setShowTask] = useState(opened);
  const [showPopup, setShowPopup] = useState(false);
  const [showSlideMenu, setShowSlideMenu] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  // variables
  const moduleWorkspaces = mainStore.moduleWorkspaces.list;
  const currentWorkspaceID = mainStore.context.workspaceID;

  // Effects
  useEffect(() => {
    setCheckboxChecked(task.resolved);
  }, [task.resolved]);

  useEffect(() => {
    if (task.id === task_id) {
      setShowTask(true);
    }
  }, []);

  useEffect(() => {
    if (task.id === selectedTaskID) {
      setShowSlideMenu(true);
    } else {
      setShowSlideMenu(false);
    }
  }, [task.id, selectedTaskID]);

  // Variables
  const { isCurrentWorkspaceActive } = mainStore.workspaces;

  const creator = mainStore.users.users.find(
    (user) => user.id === task.author.id,
  );
  const assignees = mainStore.users.users.filter((user) =>
    task.assignees_ids.includes(user.id),
  );
  const history = useHistory();
  const arrowClasses = classNames("task-arrow", { active: showTask });
  const dueDateClasses = classNames("task-parameters-value due-date", {
    "no-due-date": !task.due_date,
  });
  const associatedRecordClasses = classNames(
    "task-parameters-value",
    "parameters-associated",
    { "no-associated-record": !task.record_name },
  );
  // @ts-expect-error TS(2538) FIXME: Type 'undefined' cannot be used as an index type.
  const creatorStyle = { background: userColors[creator?.icon_color_index] };
  const nameDueDateClasses = classNames({
    "due-date": task.due_date && !task.resolved,
  });
  const tasksNameTitleClasses = classNames("tasks-name-date", {
    "task-name-illumination": task.id == task_id,
  });
  const tasksNameClasses = classNames("description-wrap task-name", {
    "task-name-illumination": task.id == task_id,
  });

  const renderDeletePopup = (
    <Popup
      trigger={<button className="button-delete-task">Delete Task</button>}
      position="bottom right"
      open={showPopup}
      onOpen={() => setShowPopup(true)}
      onClose={() => setShowPopup(false)}
      keepTooltipInside
    >
      <div className="table-dropdown" role="confirmation-dialog">
        <h4>Deleting Task</h4>
        <p>
          Are you sure you wish to delete this task? Actions are non-reversable
        </p>
        <div className="confirmation">
          <button
            onClick={handleConfirmDeleteClick}
            role="confirmation-delete-task"
          >
            Yes
          </button>
          <button
            onClick={() => setShowPopup(false)}
            role="cancel-confirmation-delete-task"
          >
            No
          </button>
        </div>
      </div>
    </Popup>
  );

  function editTask() {
    history.push(`/notifications/tasks/${task.id}/edit`);
  }

  function toggleShowTask() {
    setShowTask(!showTask);
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'e' implicitly has an 'any' type.
  function toggleReply(e) {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }

    if (task.id === selectedTaskID) {
      setSelectedTaskID(null);
    } else {
      setSelectedTaskID(task.id);
    }
  }

  async function handleConfirmDeleteClick() {
    await mainStore.comments.delete(task.id, task.module_workspace_id);
    await mainStore.tasks.myTasks({ workspace_id: workspaceID });
    mainStore.toast.setText("Task deleted!");
  }

  async function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setCheckboxChecked(!checkboxChecked);
    await mainStore.comments.update(
      task.id,
      { resolved: event.target.checked },
      task.module_workspace_id,
    );
    mainStore.tasks.myTasks(); // refetch list of Tasks
  }

  const linkButton = (
    <button className="button-edit-task" onClick={goToTask}>
      <img
        src={arrowRight}
        alt="edit-icon"
        data-testid={`view-task-button-${task.id}`}
      />
      View Record
    </button>
  );

  async function goToTask() {
    if (task.workspace_id !== currentWorkspaceID) {
      await mainStore.workspaces.switch(task.workspace_id);
    }
    history.push(task.details_view_path);
  }

  return (
    <>
      <div onClick={toggleShowTask} className="all-tasks-header-wrap">
        <div className="table-checkbox">
          <input
            type="checkbox"
            id={`task-${task.id}`}
            checked={checkboxChecked}
            onChange={handleChange}
          />
          <label htmlFor={`task-${task.id}`} />
        </div>
        <div
          className="all-tasks-header"
          data-testid={`task-in-list-${task.id}`}
        >
          <div className="all-tasks-name">
            <span className="tasks-name-initials" style={creatorStyle}>
              {creator?.initials}
            </span>
            <div onClick={toggleShowTask} className={tasksNameTitleClasses}>
              <MentionsInput disabled value={task.content} className="mentions">
                <Mention trigger="@" data={[]} />
              </MentionsInput>
              <div className="task-modules-wrap">
                <p className={nameDueDateClasses}>
                  {task.due_date && !task.resolved
                    ? `Due ${formatTimeAfterDate(
                        stringToDateAndTime(task.due_date),
                      )}`
                    : !task.resolved
                    ? "No Due Date"
                    : "Completed"}
                </p>
                <div className="task-cw-modules-block">
                  {workspaceID === 0 && renderWorkspaces(task.workspace_id)}
                  <div className="task-modules-block">
                    <img
                      src={iconForThemisModuleIdentifier(
                        task.themis_module_identifier,
                      )}
                    />
                    {nameFromModuleWorkspace(
                      task.themis_module_identifier,
                      moduleWorkspaces,
                    )}
                  </div>
                  <div className="task-modules-block">
                    {task.private && <MiniTag fontSize="xs" label="Internal" />}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="task-button-flex">
            <button className="reply-button" onClick={toggleReply}>
              <img src={replyIcon} alt="reply-icon" />
              Reply ({task.replies.length})
            </button>
            <div
              onClick={toggleShowTask}
              className="task-arrow-wrap"
              data-testid={`task-arrow-wrap-${task.id}`}
            >
              <img className={arrowClasses} src={arrowDown} alt="arrow-down" />
            </div>
          </div>
        </div>
      </div>

      {showTask && (
        <div className="all-tasks-description">
          <h5>Task Description:</h5>
          <div className={tasksNameClasses}>
            <MentionsInput
              disabled
              value={task.content}
              className="mentions single-tasks-mentions"
            >
              <Mention trigger="@" data={[]} />
            </MentionsInput>
          </div>
          <div className="all-tasks-columns">
            <div className="tasks-columns-block">
              <div className="task-parameters">
                <h5>Due Date:</h5>
                <div className={dueDateClasses}>
                  {task.due_date
                    ? formatTimeAfterDate(stringToDateAndTime(task.due_date))
                    : "N/A"}
                </div>
              </div>
              <div className="task-parameters">
                <h5>Creation Date:</h5>
                <div className="task-parameters-value">
                  {formatTimeAfterDate(stringToDateAndTime(task.created_at))}
                </div>
              </div>
            </div>
            <div className="tasks-columns-block">
              <div className="task-parameters">
                <h5>Associated Record:</h5>
                <h4 className="global-label small-label">{task?.global_id}</h4>
                <div className={associatedRecordClasses}>
                  {_.truncate(task?.record_name, { length: 25 }) || "N/A"}
                </div>
              </div>
              <div className="task-parameters">
                <h5>Creator:</h5>
                <div className="task-parameters-value parameters-initials">
                  <span style={creatorStyle}>{creator?.initials}</span>
                  {creator?.full_name}
                </div>
              </div>
            </div>
          </div>
          <div className="task-parameters-assignee">
            <h5> {assignees.length === 1 ? "Assignee:" : "Assignees:"}</h5>
            {assignees.length > 0 ? (
              <>
                {assignees.slice(0, 3).map((assignee) => (
                  <div
                    className="task-parameters-value parameters-initials"
                    key={assignee.id}
                  >
                    <span
                      style={{
                        background: userColors[assignee.icon_color_index],
                      }}
                    >
                      {assignee?.initials}
                    </span>
                    {assignee?.full_name}
                  </div>
                ))}
                {assignees.length > 3 && (
                  <div className="more-assignees">
                    +{assignees.length - 3} more assignees
                  </div>
                )}
              </>
            ) : (
              <span className="no-assignee">N/A</span>
            )}
          </div>
          {!task.resolved && (
            <div className="all-tasks-buttons">
              <button className="button-view-task" onClick={() => editTask()}>
                <img src={eyeIcon} alt="edit-icon" />
                View/Edit Task
              </button>
              {linkButton}
              {task.user_can_destroy &&
                isCurrentWorkspaceActive &&
                renderDeletePopup}
            </div>
          )}
        </div>
      )}

      <SlideMenu open={showSlideMenu} singleSlideMenu>
        <ImmutableTaskComment
          taskID={task.id}
          closeSlideMenu={toggleReply}
          workspaceID={workspaceID}
        />
      </SlideMenu>
    </>
  );
}

SingleTask.defaultProps = {
  opened: false,
};

export default observer(SingleTask);
